import {
  Component,
  Input,
  ViewChild,
  OnDestroy,
  OnChanges,
  EventEmitter,
  Output,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import ImageViewer from 'iv-viewer';
//import 'iv-viewer/dist/iv-viewer.css';

@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  imports: [FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ImageViewerComponent implements OnDestroy, OnChanges {
  @Input() imgUrl: string;
  @Input() totalPages: number;

  ZOOM_MAX = 500;
  ZOOM_MIN = 100;
  ZOOM_INCR = 40;
  ROTATE_DEGREES = 90;

  zoomPercent = 100;
  viewer: any;

  currentRotation = 0;
  imageVertical = true;

  page = 1;

  @Output() pageSelection: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('imageContainer', { static: true }) imageContainer: ElementRef;

  ngOnChanges(changes): void {
    if (changes.imgUrl && this.imgUrl) {
      this.loadImage();
    }
  }

  loadImage(): void {
    if (!this.viewer) {
      this.viewer = new ImageViewer(this.imageContainer.nativeElement, {});
    }
    this.viewer.load(this.imgUrl, this.imgUrl);
  }

  zoomIn(): void {
    if (this.zoomPercent >= this.ZOOM_MAX) {
      return;
    }
    this.zoomPercent += this.ZOOM_INCR;
    this.viewer.zoom(this.zoomPercent);
  }

  zoomOut() {
    if (this.zoomPercent <= this.ZOOM_MIN) {
      return;
    }
    this.zoomPercent -= this.ZOOM_INCR;
    this.viewer.zoom(this.zoomPercent);
  }

  resetZoom() {
    this.zoomPercent = this.ZOOM_MIN;
    this.viewer.zoom(this.zoomPercent);
  }

  rotateLeft() {
    this.currentRotation -= this.ROTATE_DEGREES;
    this.rotate();
  }

  rotateRight() {
    this.currentRotation += this.ROTATE_DEGREES;
    this.rotate();
  }

  rotate() {
    this.resetZoom();
    this.imageVertical = !this.imageVertical;

    const transformVal = `rotate(${this.currentRotation}deg)`;
    this.setImageStyles(transformVal);
  }

  setImageStyles(value: string) {
    this.setStyle('iv-snap-image', 'webkitTransform', value);
    this.setStyle('iv-snap-image', 'mozTransform', value);
    this.setStyle('iv-snap-image', 'msTransform', value);
    this.setStyle('iv-snap-image', 'transform', value);

    this.setStyle('iv-large-image', 'webkitTransform', value);
    this.setStyle('iv-large-image', 'mozTransform', value);
    this.setStyle('iv-large-image', 'msTransform', value);
    this.setStyle('iv-large-image', 'transform', value);
  }

  setStyle(cls: string, styleName: string, val: string): void {
    const listElements = this.imageContainer.nativeElement.getElementsByClassName(cls);

    for (const el of listElements) {
      el.style[styleName] = val;
    }
  }

  get stepForwardEnabled() {
    return this.page < this.totalPages;
  }

  get stepBackwardEnabled() {
    return this.page > 1;
  }

  get zoomInEnabled() {
    return this.zoomPercent < this.ZOOM_MAX;
  }

  get zoomOutEnabled() {
    const marginOfError = 2;
    return this.zoomPercent > this.ZOOM_MIN + marginOfError;
  }

  nextPage(): void {
    this.page++;
    this.changePage();
  }

  prevPage(): void {
    this.page--;
    this.changePage();
  }

  changePage(): void {
    this.pageSelection.emit(this.page - 1);
  }

  ngOnDestroy(): void {
    this.viewer = this.viewer.destroy();
  }

  // Underlying ImageViewer handles mouse wheel scrolling
  // We need to ensure we have the latest zoom value to properly handle zoom features
  public onWheel() {
    this.zoomPercent = this.viewer._state.zoomValue;
  }
}
